import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useArchiveCustomer } from '@/routes/customers/useCustomerActions';

/**
 * Archive confirmation — the third half of M1-30, alongside create and edit.
 *
 * Idempotent on the API side (M1-01's Archive handler just confirms an
 * already-archived customer), so this never needs to guard against a double
 * click landing twice — but the button that opens it is hidden once the
 * customer is already archived (`CustomerDetail`), because offering to
 * archive something already archived is a confusing "why is this still
 * here" rather than a genuinely idempotent, safe-to-repeat action.
 */
export function ArchiveCustomerDialog({
  customerId,
  customerName,
  onClose,
  onArchived,
}: {
  customerId: string;
  customerName: string;
  onClose: () => void;
  onArchived: () => void;
}) {
  const { t } = useTranslation();
  const archiveCustomer = useArchiveCustomer(customerId);

  return (
    <ConfirmDialog
      titleId="archive-customer-title"
      title={t('customers.archiveCustomer.title')}
      description={t('customers.archiveCustomer.description', { name: customerName })}
      confirmLabel={
        archiveCustomer.isPending
          ? t('customers.archiveCustomer.archiving')
          : t('customers.archiveCustomer.confirm')
      }
      isPending={archiveCustomer.isPending}
      errorMessage={archiveCustomer.isError ? t('customers.archiveCustomer.failed') : undefined}
      testId="archive-customer-dialog"
      onClose={onClose}
      onConfirm={() =>
        archiveCustomer.mutate(undefined, {
          onSuccess: () => {
            onArchived();
            onClose();
          },
        })
      }
    />
  );
}
